<template>
  <FriedModalSubContainer>
    <template #header>
      <FriedModalHeader :header="t('sign-using-bankid')" />
    </template>
    <div :class="['eid-holder', { 'criipto-button-holder-height': showCriiptoButton }]">
      <div class="eid-selector">
        <FriedParagraph class="bank-id-description">
          {{ t('the-sender-of-the-document-want-you-to-sign-this-document-with-bankid') }}
        </FriedParagraph>
        <FriedDropdown :append-to-body="false" :same-width-as-ref="true" class="eid-dropdown">
          <template #trigger="{ open }">
            <FriedFieldSet
              class="eid-container"
              :label="t('electronic-identification')"
              :active="true"
            >
              <div class="dropdown-input" data-external="eid-modal-bankid-dropdown-input">
                <FriedParagraph class="name">
                  {{ placeholder }}
                </FriedParagraph>
                <FriedIcon :class="['chevron-icon', { expanded: open }]" icon="chevron-down" />
              </div>
            </FriedFieldSet>
          </template>
          <template #content>
            <div>
              <BankIdOption
                v-for="option in options"
                v-show="option.key"
                :key="option.key"
                :selected="selected(option)"
                tabindex="0"
                :option="option"
                @select="handleFirstRecipientsInput"
              />
            </div>
          </template>
        </FriedDropdown>
      </div>
      <CriiptoButton
        v-if="showCriiptoButton"
        :selected-eid="selectedEid"
        :status="criiptoStatus"
        @sign="handleCriiptoRedirect"
      />
      <BankIdSe v-else-if="showBankIDSe" :bank-id="bankId" :jwt="jwt" @sign="handleSignDocument" />
    </div>
    <template #footer>
      <FriedModalButtonsFooter>
        <FriedButton
          v-if="bankId.step === BankIdStep.Failed"
          id="bankid-reset-button"
          data-external="eid-error-try-again-button"
          :button-type="ButtonType.Secondary"
          :disabled="bankId.isLoading"
          @click="signingStore.resetBankId()"
        >
          {{ t('try-again') }}
        </FriedButton>
        <FriedButton
          v-if="bankIdStarted"
          id="back-polling-button"
          data-external="eid-back-button"
          :disabled="bankId.isLoading"
          :button-type="ButtonType.Secondary"
          @click="signingStore.resetBankId()"
        >
          {{ t('back') }}
        </FriedButton>
        <FriedButton
          v-if="bankId.step !== BankIdStep.Failed"
          data-external="eid-cancel-button"
          :disabled="bankId.isLoading"
          @click="handleClose"
        >
          {{ t('cancel') }}
        </FriedButton>
      </FriedModalButtonsFooter>
    </template>
  </FriedModalSubContainer>
</template>

<script lang="ts">
import { ButtonType } from '@getaccept/fried-tofu';
import type { Channel } from '@getaccept/pusher';
import { RecipientEvent } from '@getaccept/pusher';
import type { PropType } from 'vue';
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { userLanguage } from '../../../lang';
import { pusherClient } from '../../../pusher';
import { useSigningStore } from '../../../signing/store/signing.store';
import { getIdOptions } from '../../constants/eid-options';
import type { Id } from '../../types/eid';
import { BankIdSigningType, BankIdStep } from '../../types/enums/bankid-se';
import { CriiptoStatus } from '../../types/enums/criipto-status';
import { EidType } from '../../types/enums/eid-type';
import BankIdOption from '../BankIdOption.vue';
import BankIdSe from './BankIdSe.vue';
import CriiptoButton from './CriiptoButton.vue';

export default defineComponent({
  components: {
    BankIdSe,
    BankIdOption,
    CriiptoButton,
  },
  props: {
    documentId: {
      type: String,
      required: true,
    },
    recipientId: {
      type: String,
      default: '',
    },
    authId: {
      type: String,
      default: '',
    },
    jwt: {
      type: String,
      default: '',
    },
    reviewPending: {
      type: Boolean,
    },
    recipientPusherChannel: {
      type: Object as PropType<Channel>,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const openBankIdPicker = ref(false);
    const options = ref<Id[]>([]);

    const signingStore = useSigningStore();

    const { bankId, criiptoStatus, selectedEid } = storeToRefs(signingStore);

    const setBankIdState = (payload: { step: BankIdStep; signingType: BankIdSigningType }) => {
      signingStore.setBankIdState(payload.step, payload.signingType);
    };

    const setCriiptoStatus = (status: CriiptoStatus) => {
      signingStore.setCriiptoStatus(status);
    };

    const setSelectedEid = (eid: Id) => {
      signingStore.setSelectedEid(eid);
    };

    const criiptoRedirect = () => {
      signingStore.redirectCriipto();
    };

    const handleWatchCriiptoStatus = (status: CriiptoStatus) => {
      if (status === CriiptoStatus.Success) {
        handleSignDocument();
      }
    };

    watch(criiptoStatus, handleWatchCriiptoStatus);

    const placeholder = computed(() => selectedEid.value?.name || t('select-an-electronic-id'));

    const showBankIDSe = computed(
      () => !!selectedEid.value && selectedEid.value.key === EidType.SE
    );

    const showCriiptoButton = computed(() =>
      [EidType.MitID, EidType.BankIDFI, EidType.NO].includes(selectedEid.value?.key)
    );

    const showEidPicker = computed(() => criiptoStatus.value !== CriiptoStatus.Loading);

    const bankIdStarted = computed(() => bankId.value.signingType !== BankIdSigningType.None);

    const selected = (option: Id) => selectedEid.value?.key === option.key;

    const defaultBankIdOption = (userLang: string): Id => {
      switch (userLang) {
        case 'sv':
          return options.value.find(option => option.key === EidType.SE);
        case 'da':
          return options.value.find(option => option.key === EidType.MitID);
        case 'no':
        case 'nn':
        case 'nb':
          return options.value.find(option => option.key === EidType.NO);
        case 'fi':
          return options.value.find(option => option.key === EidType.BankIDFI);
        default:
          return null;
      }
    };

    const handleRecipientsInput = (selectedEid: Id) => {
      signingStore.resetBankId();
      setSelectedEid(selectedEid);
      openBankIdPicker.value = false;
    };

    const handleFirstRecipientsInput = (selectedEid: Id) => {
      setSelectedEid(selectedEid);
      addCriiptoSubscription();
      openBankIdPicker.value = false;
    };

    const handleCriiptoRedirect = () => {
      criiptoRedirect();
    };

    const handleSignDocument = () => {
      if (props.reviewPending) {
        handleClose();
        return;
      }
      emit('sign');
      signingStore.resetBankId();
    };

    const handleToggleBankIdPicker = (open: boolean) => {
      openBankIdPicker.value = open;
    };

    const handleClose = () => {
      signingStore.resetBankId();
      emit('close');
    };

    const addCriiptoSubscription = () => {
      pusherClient.listen(
        props.recipientPusherChannel,
        RecipientEvent.CriiptoStatusUpdate,
        ({ criiptoStatus }: { criiptoStatus: CriiptoStatus }) => {
          setCriiptoStatus(criiptoStatus);
        }
      );
    };

    const setDefaultEid = () => {
      if (!selectedEid.value) {
        setSelectedEid(defaultBankIdOption(userLanguage()));
      }
    };

    onMounted(() => {
      options.value = getIdOptions();
      setDefaultEid();
      if (selectedEid.value) {
        addCriiptoSubscription();
      }
      if (criiptoStatus.value === CriiptoStatus.Success) {
        handleSignDocument();
      }
    });

    const created = () => {
      if (bankId.value.orderRef) {
        signingStore.startBankIdPolling();
      }
    };

    created();

    return {
      ButtonType,
      BankIdStep,
      t,
      showCriiptoButton,
      showBankIDSe,
      showEidPicker,
      bankIdStarted,
      selected,
      placeholder,
      options,
      criiptoStatus,
      selectedEid,
      openBankIdPicker,
      handleRecipientsInput,
      handleFirstRecipientsInput,
      handleCriiptoRedirect,
      handleSignDocument,
      handleToggleBankIdPicker,
      handleClose,
      bankId,
      handleWatchCriiptoStatus,
      setBankIdState,
      setCriiptoStatus,
      setSelectedEid,
      criiptoRedirect,
      defaultBankIdOption,
      signingStore,
    };
  },
});
</script>
<style lang="scss" scoped>
.bank-id-description {
  margin-top: 0;
}

.eid-dropdown :deep(.floater-reference-container) {
  display: block;
}

.criipto-button-holder-height {
  height: 80%;
}

.eid-holder {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-150);

  .eid-selector {
    .eid-container {
      cursor: pointer;
      width: 100%;
      margin-top: var(--spacing-100);

      .dropdown-input {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 0 var(--spacing-75);

        .name {
          flex: 1 1 100%;
        }

        .chevron-icon {
          transition: all 0.2s ease-in-out;

          &.expanded {
            transform: rotate(180deg);
          }
        }
      }
    }
  }

  .eid-label {
    display: block;
    margin: var(--spacing-150) 0 var(--spacing-50) 0;
  }
}
</style>
